import type { Dispatch } from 'redux';
import {
    setCampaignForSubmission,
    setCategoryForSubmission,
    setSelectedProject,
    setSubmissionModalState,
    loadSubmissionPaymentCheckout,
} from '../redux/contest/actions';
import type { SubmissionPaymentCheckoutContext } from '../redux/contest/submissionPaymentCheckoutSaga';
import type { IContest, IProject, ISubmission } from '../types/global/helper';
import { dispatchPromise, showToastMessage } from './AppUtils';

export type { SubmissionPaymentCheckoutContext };

/**
 * Load unpaid-entry checkout context (payment-status + contest) via contest saga.
 */
export async function loadSubmissionPaymentCheckoutContext(
    submissionId: string,
    fallback?: { project?: IProject; contest?: IContest },
): Promise<SubmissionPaymentCheckoutContext> {
    return dispatchPromise(
        loadSubmissionPaymentCheckout({ submissionId, fallback }),
    ) as Promise<SubmissionPaymentCheckoutContext>;
}

/**
 * Open the dashboard CampaignSubmitter in pay-later mode for an unpaid submission.
 */
export async function openSubmissionPaymentCheckout(
    dispatch: Dispatch,
    submissionId: string,
    fallback?: { project?: IProject; submission?: ISubmission; contest?: IContest },
): Promise<boolean> {
    try {
        const contestHint = fallback?.contest || fallback?.submission?.contest;
        const projectHint = fallback?.project || (fallback?.submission?.project as IProject | undefined) || undefined;
        const ctx = await loadSubmissionPaymentCheckoutContext(submissionId, {
            project: projectHint,
            contest: contestHint,
        });

        dispatch(setCampaignForSubmission(ctx.contest));
        if (ctx.project?._id) {
            dispatch(setSelectedProject(ctx.project));
        }
        if (ctx.category) {
            dispatch(setCategoryForSubmission(ctx.category));
        }
        dispatch(
            setSubmissionModalState({
                open: true,
                selectedCategory: ctx.category,
                chosen_tier: ctx.chosen_tier,
                existingSubmissionId: ctx.submissionId,
                saleObject: ctx.saleObject,
                currencyInfo: ctx.currencyInfo,
            }),
        );
        showToastMessage('Complete payment to keep your entry eligible.', 'info');
        return true;
    } catch (err: any) {
        const code = err?.code as string | undefined;
        if (code === 'ALREADY_PAID' || code === 'NO_PAYMENT_DUE') {
            showToastMessage(err.message, 'info');
        } else {
            showToastMessage(err?.message || 'Could not load submission for payment.', 'error');
        }
        return false;
    }
}
